import "./login.css";
import React, { useEffect } from 'react';
import translations from '../translations';
import config from '../config';

const LoginPage = () => {

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        // B2C redirects the OAuth2 callback here as /login?code=...&state=...
        // Always hand the code to oauth2-proxy to redeem it. Do NOT set
        // 'authCompleted' here: marking auth complete before the session actually
        // exists poisons sessionStorage if the callback fails, which traps the user
        // in an infinite /login ⇄ /dtc-bot loop (the code branch never re-runs
        // because the flag is already true).
        if (code && state) {
            window.location.href = `/oauth2/callback?code=${code}&state=${state}`;
            return;
        }

        // No code in the URL → ask oauth2-proxy whether a real session exists.
        // The session cookie is the single source of truth: only enter the app if
        // /oauth2/userinfo confirms a valid session, otherwise stay on the login page.
        fetch('/oauth2/userinfo', { credentials: 'include' })
            .then((res) => {
                if (res.ok) {
                    sessionStorage.setItem('authCompleted', 'true');
                    window.location.href = '/dtc-bot';
                } else {
                    sessionStorage.removeItem('authCompleted');
                }
            })
            .catch(() => sessionStorage.removeItem('authCompleted'));
    }, []);

    // Check if running on localhost or local network IP
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'

    // Direct login for localhost only
    const handleLocalLogin = () => {
        const devConfig = config.development;
        sessionStorage.setItem('authCompleted', 'true');
        localStorage.setItem('userEmail', devConfig.userEmail);
        window.location.href = '/dtc-bot';
    };

    // OAuth2 login - redirect to oauth2-proxy start endpoint
    // oauth2-proxy will redirect to Microsoft Azure AD for authentication
    const handleLogin = () => {
        sessionStorage.clear();
        // rd=/login so the post-callback landing returns to this page, which then
        // verifies the session via /oauth2/userinfo before entering /dtc-bot. This
        // keeps 'authCompleted' driven by the real cookie, not an optimistic guess.
        window.location.href = "/oauth2/start?rd=/login";
    };

    // OPTION 2: If you want to completely skip the login page,
    // uncomment the useEffect below and comment out the current one
    /*
    useEffect(() => {
        const devConfig = config.development;
        // Auto-bypass on page load
        sessionStorage.setItem('authCompleted', 'true');
        // Store user email in localStorage for local development
        localStorage.setItem('userEmail', devConfig.userEmail);
        window.location.href = '/dtc-bot';
    }, []);
    */

    const translate = (text) => {
        if (!text || !translations[text]) return text;

        switch ('Japanese') {
            case 'Japanese':
                return translations[text].translationJp || text;
            case 'Indonesian':
                return translations[text].translationIn || text;
            case 'English':
            default:
                return translations[text].translationEng || text;
        }
    };

    return (
        <div className="login">
            <div className="login-top">
                <img className="logo" src="./login-fuso.svg" alt="fuso-logo"></img>
            </div>
            <div className="login-mid">
                <div className="left-side">
                    <img className="botImgDTCLogin" src="./login-bot.svg" alt="bot-image"></img>
                    <div className="portal-name">
                        <p>ようこそ <span>DTCチャットボットポータル</span></p>
                    </div>
                </div>
                {isLocalhost ? (
                    <div onClick={handleLocalLogin} className="login-button">
                        <img src="./microsoft.svg" alt="outlook-logo"></img>
                        <span>Microsoftアカウントでログイン</span>
                    </div>
                ) : (
                    <div onClick={handleLogin} className="login-button">
                        <img src="./microsoft.svg" alt="outlook-logo"></img>
                        <span>Microsoftアカウントでログイン</span>
                    </div>
                )}
            </div>
            <div className="login-bottom">
                <img src="./back-image.svg" className="background-image" alt="base-image"></img>
                <div className="features">
                    <div className="feature-card">
                        <div className="icon-wrapper">
                            <img src="./login-avail.svg" alt="feature-availability"></img>
                        </div>
                        <h3>{translate("24/7 Availability")}</h3>
                        <span>{translate("TIC Chatbots provide round-the-clock support.")}</span>
                    </div>
                    <div className="feature-card">
                        <div className="icon-wrapper">
                            <img src="./login-response.svg" alt="feature-availability"></img>
                        </div>
                        <h3>{translate("Instant Response")}</h3>
                        <span>{translate("Deliver immediate replies to user inquiries.")}</span>
                    </div>
                    <div className="feature-card">
                        <div className="icon-wrapper">
                            <img src="./login-consistency.svg" alt="feature-availability"></img>
                        </div>
                        <h3>{translate("Consistency")}</h3>
                        <span>{translate("Provide consistent responses & follow scripts.")}</span>
                    </div>
                    <div className="feature-card">
                        <div className="icon-wrapper">
                            <img src="./login-scalability.svg" alt="feature-availability"></img>
                        </div>
                        <h3>{translate("Scalability")}</h3>
                        <span>{translate("Handle numerous interactions simultaneously.")}</span>
                    </div>
                </div>
                <div className="disclaimer-body">
                    <div className="disclaimer-box">
                        【注意事項】
                        <ul>
                            <li>DTC Chatbotは生成AIを使用した車両修理に関する技術支援システムです</li>
                            <li>多くの利用を経験することで生成AIは学習し、より精度の高い回答を出せるよう成長する</li>
                            <li>回答は全てが十分ではないため、精度の高い回答を得られない場合がある</li>
                        </ul>
                    </div>
                    <div className="disclaimer-box">
                        【依頼事項】
                        <ul>
                            <li>回答には社外秘の情報を含むため、そのままの回答を外部に持ち出さない</li>
                            <li>具体的な質問であれば回答の具体的になるので、より具体的に質問の記載する</li>
                            <li>DTC Chatbotを使用しも不具合の解決に至る答えが出ない場合はDTCに相談する</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;